import {
  changeUser,
  initialize as initializeBraze,
  subscribeToContentCardsUpdates,
  removeSubscription,
  openSession,
  requestContentCardsRefresh,
  type ContentCards,
  type Card,
} from '@braze/web-sdk';
import { orderBy } from 'lodash-es';

type SubscriptionCallback = (cards: ContentCards) => void;

let subscriptionState:
  | {
      subscriptions: SubscriptionCallback[];
      unsubscribeIdentifier: string;
      timeout?: ReturnType<typeof setTimeout>;
    }
  | {
      unsubscribeIdentifier: undefined;
    } = {
  unsubscribeIdentifier: undefined,
};

function onCardsUpdate(cards: ContentCards) {
  if (subscriptionState.unsubscribeIdentifier === undefined) {
    return;
  }
  const { subscriptions } = subscriptionState;
  subscriptions.forEach((cb) => {
    cb(cards);
  });
}

function poll() {
  requestContentCardsRefresh(() => {
    if (subscriptionState.unsubscribeIdentifier === undefined) {
      return;
    }
    subscriptionState.timeout = setTimeout(poll, 5000);
  });
}

function unsubscribeHandler(cb: SubscriptionCallback) {
  if (subscriptionState.unsubscribeIdentifier === undefined) {
    return;
  }

  const { subscriptions } = subscriptionState;

  subscriptionState.subscriptions = subscriptions.filter((arg) => arg !== cb);

  if (subscriptionState.subscriptions.length > 0) {
    return;
  }

  resetSubscriptions();
}

export const subscribeContentCards = (cb: SubscriptionCallback) => {
  if (subscriptionState.unsubscribeIdentifier === undefined) {
    const unsubId = subscribeToContentCardsUpdates(onCardsUpdate);
    if (unsubId === undefined) {
      // subscription not successful
      // Unsure how to handle
      return () => void 0;
    }
    subscriptionState = {
      unsubscribeIdentifier: unsubId,
      subscriptions: [],
    };
    openSession();
    poll();
  }

  const { subscriptions } = subscriptionState;
  subscriptions.push(cb);
  return () => {
    unsubscribeHandler(cb);
  };
};

export function resetSubscriptions() {
  // everything is unsubscribed
  const { unsubscribeIdentifier } = subscriptionState;
  if (unsubscribeIdentifier === undefined) {
    return;
  }

  removeSubscription(unsubscribeIdentifier);

  const { timeout } = subscriptionState;
  if (timeout !== undefined) {
    clearTimeout(timeout);
  }

  subscriptionState = {
    unsubscribeIdentifier: undefined,
  };
}

export function init({
  apiKey,
  baseUrl,
  userId,
}: {
  apiKey: string;
  baseUrl: string;
  userId: number;
}) {
  const initializeOptions: Parameters<typeof initializeBraze>[1] = {
    baseUrl,
  };

  if (process.env.NODE_ENV === 'development') {
    initializeOptions.enableLogging = true;
  }

  initializeBraze(apiKey, initializeOptions);
  changeUser(`${userId}`);

  return resetSubscriptions;
}

function sortByPriority(filtered: Card[]) {
  return orderBy(filtered, ['extras.priority'], ['asc']);
}

const validDeviceTypes = Object.freeze([
  undefined,
  'all',
  'mweb',
  'desktopweb',
]);

export function getVisibleCards(
  location: string,
  allBrazeCards: ContentCards['cards']
) {
  const filtered = allBrazeCards.filter((card: Card) => {
    return (
      card.extras?.location === location &&
      validDeviceTypes.includes(card.extras?.device_type)
    );
  });

  return sortByPriority(filtered);
}
